/**
 * Schedule resolution: a pure function from the timeline and the list of actions (each with a timestamp) to the
 * segments that animate, the visibility and data-version changes over time, and each action's result.
 *
 * Rules (TaskBrief, Animation and the OGraf step model):
 * - a new action instantly completes every running animation, then runs; no action is dropped;
 * - a transport action (play, stop) cancels anything its predecessors scheduled for later (a pending step loop,
 *   a sting's automatic out); an update or custom action keeps it, moved earlier by the time it saved;
 * - hold loops repeat until the next transport action and never delay a promise;
 * - skipAnimation completes the action's animations at the instant it is applied.
 * Real-time and non-real-time use the same resolution: real time stamps actions with performance.now(),
 * non-real time with the schedule's timestamps.
 */
import type { Easing, Phase, Timeline, Track } from '@prism-graphics/scene-schema';
import { targetStep } from './steps.ts';

export type ActionType = 'playAction' | 'stopAction' | 'updateAction' | 'customAction';

export interface EngineAction {
  /** ms on the engine clock. */
  t: number;
  /** Tie-breaker for equal timestamps (insertion order). */
  seq: number;
  type: ActionType;
  params: {
    goto?: number;
    delta?: number;
    skipAnimation?: boolean;
    id?: string;
  };
  /** updateAction: the incoming (partial) data; not used by the resolution itself. */
  data?: unknown;
  /** updateAction: the data version this update produces. */
  version?: number;
  /** updateAction: ids of the fields whose value changed. */
  changedFields?: readonly string[];
}

export type SegmentKind = 'in' | 'step' | 'loop' | 'out' | 'update' | 'custom';

export interface Segment {
  kind: SegmentKind;
  tracks: readonly Track[];
  start: number;
  duration: number;
  /** When the segment stops changing: start + duration, earlier if completed by a later action; Infinity for a running loop. */
  end: number;
  loop: boolean;
  /** Index (in sorted order) of the action that created it; -1 for segments carried over by pruning. */
  action: number;
  dropped: boolean;
  /** A play from the start or end state starts from the designed state: earlier segments stop counting then. */
  until?: number;
  /** Crossfade: these elements show data version `from` fading out under the current data fading in. */
  fade?: { from: number; elements: readonly string[]; easing: Easing };
}

export interface ActionResult {
  statusCode: number;
  statusMessage?: string;
  /** Step after the action; undefined in the start/end state and for stepCount 0 graphics. */
  currentStep: number | undefined;
  /** When the action's promise should resolve (the graphic is ready for the next action). */
  settleAt: number;
}

export interface TimedFlag {
  t: number;
  order: number;
  visible: boolean;
}

export interface DataSwitch {
  t: number;
  order: number;
  version: number;
}

export type GraphicState = 'start' | 'visible' | 'end';

export interface Resolution {
  /** Sorted by start, then creation. Dropped segments are removed. */
  segments: Segment[];
  visibility: TimedFlag[];
  dataSwitches: DataSwitch[];
  /** Results in the input order of `actions`. */
  results: ActionResult[];
  /** Last time at which anything changes (Infinity if a loop runs). */
  settledAfter: number;
  /** Last time at which anything but a running hold loop changes (and the last action's time). */
  quietAfter: number;
  /** State after the last action. */
  state: GraphicState;
  step: number | undefined;
  version: number;
  /** Data version before the first data switch. */
  initialVersion: number;
}

/** Where a resolution starts: the start state, or what pruning carried over from earlier actions. */
export interface Initial {
  segments: readonly Segment[];
  state: GraphicState;
  step: number | undefined;
  version: number;
  /** When the graphic became visible, if it is visible (the video clock, ADR 0029); before any action otherwise. */
  visibleSince?: number;
}

export type Box = { w: number; h: number };

/** Values bound by fields that a crossfade blends (element id → colour and/or opacity). */
export type Bound = Readonly<Record<string, { color?: string; opacity?: number }>>;

/** A field binding: which element property a field drives. */
export type FieldBinding = { element: string; property: string };

export interface ResolveInput {
  timeline: Timeline;
  stepCount: number;
  actions: readonly EngineAction[];
  /** Measured auto-size boxes per data version (element id → size). */
  boxes: (version: number) => Readonly<Record<string, Box>>;
  /** Each field's bindings, for crossfade transitions. */
  bindings: ReadonlyMap<string, readonly FieldBinding[]>;
  /** Colours and opacities bound per data version, for crossfade transitions. */
  bound?: (version: number) => Bound;
  /** Carried-over state (pruning); the start state when absent. */
  initial?: Initial;
}

export function resolveSchedule(input: ResolveInput): Resolution {
  const { timeline, stepCount } = input;
  const order = input.actions.map((a, i) => ({ a, i })).sort((x, y) => x.a.t - y.a.t || x.a.seq - y.a.seq);

  const segments: Segment[] = [];
  const visibility: TimedFlag[] = [];
  const dataSwitches: DataSwitch[] = [];
  const results: ActionResult[] = new Array(input.actions.length);
  const outs: Segment[] = [];
  const droppedAt = new Map<number, number>();
  const sortedIndexOf: number[] = new Array(input.actions.length);

  const init = input.initial;
  let state: GraphicState = init?.state ?? 'start';
  let step: number | undefined = init?.step;
  let stingOut: Segment | undefined;
  let version = init?.version ?? 0;
  if (init) {
    for (const s of init.segments) segments.push({ ...s });
    // The carried state is only evaluated at or after the prune instant, which is never before it became visible:
    // keep the real instant so the video clock (time since visible) runs on across the prune.
    if (state === 'visible') visibility.push({ t: init.visibleSince ?? -Infinity, order: -1, visible: true });
  }

  const add = (s: Omit<Segment, 'dropped' | 'end'> & { end?: number }): Segment => {
    const seg: Segment = { ...s, end: s.end ?? (s.loop ? Infinity : s.start + s.duration), dropped: false };
    segments.push(seg);
    return seg;
  };

  /** Coming back from the start or end state: what earlier actions animated no longer holds from T on. */
  const clear = (T: number) => {
    for (const seg of segments) if (seg.until === undefined) seg.until = T;
  };

  /** Lay phases out back to back from `t`; returns the end time. */
  const chain = (t: number, ai: number, skip: boolean, parts: Array<[SegmentKind, Phase | undefined]>): number => {
    let cursor = t;
    for (const [kind, phase] of parts) {
      if (!phase) continue;
      const duration = skip ? 0 : phase.duration;
      add({ kind, tracks: phase.tracks, start: cursor, duration: phase.duration, end: cursor + duration, loop: false, action: ai });
      cursor += duration;
    }
    return cursor;
  };

  const startLoop = (t: number, ai: number, s: number | undefined) => {
    const loop = s === undefined ? undefined : timeline.steps[s]?.loop;
    if (loop && loop.duration > 0) add({ kind: 'loop', tracks: loop.tracks, start: t, duration: loop.duration, loop: true, action: ai });
  };

  order.forEach(({ a, i: inputIndex }, ai) => {
    const T = a.t;
    const skip = a.params.skipAnimation === true;
    const transport = a.type === 'playAction' || a.type === 'stopAction';

    // The sting's automatic out has started (or finished): the graphic is on its way to the end state.
    if (stingOut && !stingOut.dropped && stingOut.start <= T) {
      state = 'end';
      step = undefined;
      stingOut = undefined;
    }

    // 1. Complete everything running; cancel or move what is scheduled later.
    const saved = new Map<number, number>();
    for (const seg of segments) {
      if (seg.dropped || seg.start > T || seg.end <= T) continue;
      if (seg.loop) {
        if (transport) seg.end = T;
        continue;
      }
      saved.set(seg.action, Math.max(saved.get(seg.action) ?? 0, seg.end - T));
      seg.end = T;
    }
    for (const seg of segments) {
      if (seg.dropped || seg.start <= T) continue;
      if (transport) {
        seg.dropped = true;
        if (!droppedAt.has(seg.action)) droppedAt.set(seg.action, T);
      } else {
        const by = Math.min(saved.get(seg.action) ?? 0, seg.start - T);
        seg.start -= by;
        seg.end -= by;
      }
    }
    if (transport && stingOut?.dropped) stingOut = undefined;

    // 2. Apply the action.
    const result: ActionResult = { statusCode: 200, currentStep: step, settleAt: T };
    const visible = state === 'visible';
    switch (a.type) {
      case 'playAction': {
        if (stepCount === 0) {
          if (!visible) {
            clear(T);
            visibility.push({ t: T, order: ai, visible: true });
            let cursor = chain(T, ai, skip, [['in', timeline.in]]);
            cursor += timeline.autoOutAfter ?? 0;
            const outDur = skip ? 0 : timeline.out.duration;
            stingOut = add({ kind: 'out', tracks: timeline.out.tracks, start: cursor, duration: timeline.out.duration, end: cursor + outDur, loop: false, action: ai });
            outs.push(stingOut);
            state = 'visible';
          } else {
            outs.push(...segmentsOf(ai, chain, T, skip, [['out', timeline.out]], segments));
            state = 'end';
          }
          step = undefined;
          result.currentStep = undefined;
          break;
        }
        const target = targetStep(visible ? step : undefined, stepCount, a.params.goto, a.params.delta);
        if (target === undefined) {
          if (visible) {
            outs.push(...segmentsOf(ai, chain, T, skip, [['out', timeline.out]], segments));
            state = 'end';
          }
          step = undefined;
        } else if (!visible) {
          clear(T);
          visibility.push({ t: T, order: ai, visible: true });
          const end = chain(T, ai, skip, [
            ['in', timeline.in],
            ['step', target > 0 ? timeline.steps[target]?.enter : undefined],
          ]);
          startLoop(end, ai, target);
          state = 'visible';
          step = target;
        } else if (target !== step) {
          const end = chain(T, ai, skip, [['step', timeline.steps[target]?.enter]]);
          startLoop(end, ai, target);
          step = target;
        } else {
          // Already at the target step: nothing to animate. Restart its loop (the transport action stopped it).
          startLoop(T, ai, target);
        }
        result.currentStep = step;
        break;
      }
      case 'stopAction': {
        if (visible) {
          outs.push(...segmentsOf(ai, chain, T, skip, [['out', timeline.out]], segments));
          state = 'end';
        }
        step = undefined;
        result.currentStep = undefined;
        break;
      }
      case 'updateAction': {
        const prev = version;
        const next = a.version ?? prev;
        const u = timeline.update;
        const animate = visible && !skip && u.duration > 0;
        const changed = a.changedFields ?? [];
        dataSwitches.push({ t: T, order: ai, version: next });
        version = next;
        if (visible) {
          const tracks: Track[] = [...u.tracks];
          const fading: string[] = [];
          if (animate) {
            const from = input.boxes(prev);
            const to = input.boxes(next);
            for (const id of Object.keys(to)) {
              const f = from[id];
              const n = to[id]!;
              if (!f) continue;
              if (Math.abs(f.w - n.w) > 0.25) tracks.push(blend(id, 'width', f.w, u.duration, u.easing));
              if (Math.abs(f.h - n.h) > 0.25) tracks.push(blend(id, 'height', f.h, u.duration, u.easing));
            }
            const was = input.bound?.(prev) ?? {};
            const now = input.bound?.(next) ?? {};
            for (const f of changed) {
              if (u.fields[f]?.mode !== 'crossfade') continue;
              for (const b of input.bindings.get(f) ?? []) {
                const el = b.element;
                if (b.property === 'text' || b.property === 'image') {
                  if (!fading.includes(el)) fading.push(el);
                } else if (b.property === 'color' || b.property === 'opacity') {
                  const p = b.property;
                  const v = was[el]?.[p];
                  if (v !== undefined && v !== now[el]?.[p]) tracks.push(blend(el, p, v, u.duration, u.easing));
                }
              }
            }
          }
          if (tracks.length > 0 || u.duration > 0) {
            add({ kind: 'update', tracks, start: T, duration: u.duration, end: T + (skip ? 0 : u.duration), loop: false, action: ai });
          }
          if (fading.length > 0) {
            const seg = add({ kind: 'update', tracks: [], start: T, duration: u.duration, loop: false, action: ai });
            seg.fade = { from: prev, elements: fading, easing: u.easing };
          }
          for (const f of changed) {
            const tr = u.fields[f];
            if (tr?.mode === 'phase') add({ kind: 'update', tracks: tr.phase.tracks, start: T, duration: tr.phase.duration, end: T + (skip ? 0 : tr.phase.duration), loop: false, action: ai });
          }
        }
        result.currentStep = step;
        break;
      }
      case 'customAction': {
        const def = timeline.customActions.find((c) => c.id === a.params.id);
        if (!def) {
          result.statusCode = 404;
          result.statusMessage = `unknown custom action "${String(a.params.id)}"`;
        } else {
          chain(T, ai, skip, [['custom', def.phase]]);
        }
        result.currentStep = step;
        break;
      }
    }
    results[inputIndex] = result;
    sortedIndexOf[inputIndex] = ai;
  });

  const live = segments.filter((s) => !s.dropped);
  for (const out of outs) if (!out.dropped) visibility.push({ t: out.end, order: out.action, visible: false });
  visibility.sort((x, y) => x.t - y.t || x.order - y.order);
  dataSwitches.sort((x, y) => x.t - y.t || x.order - y.order);

  // Settle times: the end of each action's own (non-loop) segments, or when a later action cancelled them.
  const ends = new Map<number, number>();
  for (const s of live) if (!s.loop && s.action >= 0) ends.set(s.action, Math.max(ends.get(s.action) ?? -Infinity, s.end));
  results.forEach((r, inputIndex) => {
    const ai = sortedIndexOf[inputIndex]!;
    const T = order[ai]!.a.t;
    let settle = Math.max(T, ends.get(ai) ?? T);
    const cut = droppedAt.get(ai);
    if (cut !== undefined) settle = Math.max(settle, cut);
    r.settleAt = settle;
  });

  let quietAfter = order.length > 0 ? order[order.length - 1]!.a.t : -Infinity;
  for (const s of live) if (!s.loop) quietAfter = Math.max(quietAfter, s.end);
  for (const v of visibility) quietAfter = Math.max(quietAfter, v.t);
  for (const d of dataSwitches) quietAfter = Math.max(quietAfter, d.t);
  let settledAfter = quietAfter;
  for (const s of live) settledAfter = Math.max(settledAfter, s.end);

  live.sort((x, y) => x.start - y.start || segments.indexOf(x) - segments.indexOf(y));
  // The sting's automatic out decides the final state once it has run.
  if (stingOut && !stingOut.dropped) {
    state = 'end';
    step = undefined;
  }
  return { segments: live, visibility, dataSwitches, results, settledAfter, quietAfter, state, step, version, initialVersion: init?.version ?? 0 };
}

function segmentsOf(
  ai: number,
  chain: (t: number, ai: number, skip: boolean, parts: Array<[SegmentKind, Phase | undefined]>) => number,
  T: number,
  skip: boolean,
  parts: Array<[SegmentKind, Phase | undefined]>,
  all: Segment[],
): Segment[] {
  const before = all.length;
  chain(T, ai, skip, parts);
  return all.slice(before);
}

/** A generated track from a value to the element's current base value (`{ rel: 0 }`), so it never pins an old value. */
function blend(element: string, property: 'width' | 'height' | 'color' | 'opacity', from: number | string, duration: number, easing: Easing): Track {
  return {
    element,
    property,
    keyframes: [
      { time: 0, value: from, easing },
      { time: duration, value: { rel: 0 }, easing: 'linear' },
    ],
  };
}

/**
 * Pruning (long real-time sessions): once nothing but a hold loop changes after `P`, every earlier action can be
 * dropped. The finished segments that still decide a value (the last one per element and property) are carried
 * over with their original times, and so is a running loop, so evaluation at any t >= P is unchanged.
 */
export function carryOver(res: Resolution, P: number, version = res.version): Initial | undefined {
  if (!(res.quietAfter <= P)) return undefined;
  const keyOf = (t: Track) => `${t.element} ${t.property} ${t.stagger?.by ?? ''}`;
  const kept: Segment[] = [];
  const later = new Set<string>();
  for (let i = res.segments.length - 1; i >= 0; i--) {
    const seg = res.segments[i]!;
    if (seg.fade || seg.until !== undefined) continue;
    const running = seg.loop && seg.end > P;
    const tracks = running ? seg.tracks : seg.tracks.filter((t) => !later.has(keyOf(t)));
    for (const t of seg.tracks) later.add(keyOf(t));
    if (tracks.length === 0) continue;
    kept.push({ ...seg, tracks, action: -1 });
  }
  // When it last became visible (the video clock): the visibility at P, which no longer changes after it.
  let on = false;
  let since = -Infinity;
  for (const f of res.visibility) {
    if (f.t > P) break;
    if (f.visible && !on) since = f.t;
    on = f.visible;
  }
  const init: Initial = { segments: kept.reverse(), state: res.state, step: res.step, version };
  if (res.state === 'visible' && on) init.visibleSince = since;
  return init;
}
